import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useNavigate } from 'react-router-dom';
import { ColorManager, getNodeColor, generateLegendItems } from '../../utils/diagramColorUtils';
import { ZoomUtils, ZOOM_CONFIG } from './diagramZoomUtils';
import DiagramTooltip, { TooltipData, ApiData } from './DiagramTooltip';
import './diagramv2.css';
import { useDarkTheme } from '../../contexts/DarkThemeContext';
// Use require for image import to avoid TypeScript module resolution issues
const motoLogo = require('../../assets/icons/msi-logo-cmyk-v-public_pos-black-circle-white-emsignia.png');

// SessionStorage key for persisting expanded/collapsed node state
const EXPANDED_NODES_STORAGE_KEY = 'diagram-expanded-nodes';

// Helper to get a stable unique identifier for a node
const getNodeKey = (d: any): string => {
  // Use data.id if available (area/API id), otherwise fall back to name + depth
  if (d.data && d.data.id) return `${d.data.id}`;
  if (d.data && d.data.name) return `${d.depth}-${d.data.name}`;
  return `${d.depth}-${d.id}`;
};

// Save expanded node IDs to sessionStorage
const saveExpandedState = (rootNode: any) => {
  if (!rootNode) return;
  const expandedIds: string[] = [];
  rootNode.descendants().forEach((d: any) => {
    // A node is "expanded" if it currently has visible children
    if (d.children && d.children.length > 0) {
      expandedIds.push(getNodeKey(d));
    }
  });
  try {
    sessionStorage.setItem(EXPANDED_NODES_STORAGE_KEY, JSON.stringify(expandedIds));
  } catch (e) {
    // sessionStorage might be full or unavailable — silently ignore
  }
};

// Load expanded node IDs from sessionStorage
const loadExpandedState = (): Set<string> | null => {
  try {
    const stored = sessionStorage.getItem(EXPANDED_NODES_STORAGE_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

// TypeScript interfaces
interface DiagramV2Props {
  data: ApiData;
  width?: number;
  height?: number;
  duration?: number;
}

interface LegendItem {
  name: string;
  color: string;
}

// Color constants
const COLORS = {
  // UI Colors
  WHITE: '#ffffff',
  BORDER_LIGHT: '#dee2e6',
  TEXT_SECONDARY: '#495057',
  TEXT_PRIMARY: '#2C3E50',
  BACKGROUND_LIGHT: '#f8f9fa',
  BORDER_GRAY: '#e9ecef',
  TEXT_MUTED: '#6c757d',
  
  // Button Colors - Primary
  PRIMARY: '#007bff',
  PRIMARY_HOVER: '#0056b3',
  
  // Button Colors - Info
  INFO: '#17a2b8',
  INFO_HOVER: '#138496',
  
  // Button Colors - Secondary
  SECONDARY: '#6c757d',
  SECONDARY_HOVER: '#545b62',
  
  // Button Colors - Success
  SUCCESS: '#28a745',
  SUCCESS_HOVER: '#1e7e34',
  
  // D3 and SVG Colors
  STROKE_COLOR: '#8E9AAF',
  
  // API Visibility Colors
  API_INTERNAL: '#28a745',
  API_EXTERNAL: '#17a2b8',
  
  // Shadow and Effect Colors (RGBA)
  DROP_SHADOW_LIGHT: 'rgba(0,0,0,0.1)',
  DROP_SHADOW_MEDIUM: 'rgba(0,0,0,0.15)',
  DROP_SHADOW_NORMAL: 'rgba(0,0,0,0.3)',
  DROP_SHADOW_STRONG: 'rgba(0,0,0,0.4)',
} as const;

// Constants for text truncation
const MAX_NODE_NAME_LENGTH = 25; // Maximum characters before truncating node names
const TRUNCATED_NAME_LENGTH = MAX_NODE_NAME_LENGTH - 3; // Length after truncation (accounting for "...")

const DiagramV2: React.FC<DiagramV2Props> = ({ data, width = 1200, height = 800, duration = 250 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [root, setRoot] = useState<any>(null);
  const updateRef = useRef<any>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1.5);
  const zoomRef = useRef<any>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [isPulsing, setIsPulsing] = useState<boolean>(true);
  const [isLegendCollapsed, setIsLegendCollapsed] = useState<boolean>(false);
  const [legendItems, setLegendItems] = useState<LegendItem[]>([]);
  const navigate = useNavigate();
  const { isDarkTheme } = useDarkTheme();
  
  // Transition management
  const isTransitioning = useRef<boolean>(false);
  const transitionTimeouts = useRef<NodeJS.Timeout[]>([]);
  
  // Color management
  const colorManagerRef = useRef(new ColorManager());

  // Zoom utility instance
  const zoomUtilsRef = useRef<ZoomUtils | null>(null);

  // Chart dimensions
  const marginTop = 100;
  const marginRight = 20;
  const marginBottom = 20;
  const marginLeft = 20;

  // Tooltip functions
  const showTooltip = (event: any, d: any) => {
    // Show tooltip for API nodes OR any node with description/developerProgram data OR long names
    if (d.data.type === 'api' || d.data.description || d.data.developerProgram !== undefined || d.data.latestApiVersionNumber || d.data.name.length > MAX_NODE_NAME_LENGTH) {
      const tooltipData: TooltipData = {
        x: event.clientX,
        y: event.clientY,
        apiData: d.data
      };
      setTooltip(tooltipData);
    }
  };

  const hideTooltip = () => {
    // Add a small delay to allow moving to tooltip
    (window as any).tooltipTimeout = setTimeout(() => {
      setTooltip(null);
    }, 150);
  };

  const keepTooltipVisible = () => {
    // Cancel any pending hide
    clearTimeout((window as any).tooltipTimeout);
  };

  const handleViewDetails = (apiId: string) => {
    setTooltip(null);
    // Open API details in a new tab to preserve Diagram View context
    window.open(`/api-details/${apiId}`, '_blank');
  };

  // Helper function to safely clear all transitions and timeouts
  const clearAllTransitions = () => {
    // Clear all pending timeouts
    transitionTimeouts.current.forEach(timeoutId => clearTimeout(timeoutId));
    transitionTimeouts.current = [];
    
    // Simple transition interrupt
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").interrupt();
    }
    
    isTransitioning.current = false;
  };

  // Simplified safe update without locks
  const safeUpdate = (source: any) => {
    if (!updateRef.current || !root) return;
    
    try {
      updateRef.current(null, source || root);
    } catch (error) {
      console.log("Error in safeUpdate:", error);
    }
  };

  useEffect(() => {
    if (!data) return; // Don't render if no data is provided
    
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Create hierarchy from provided data
    const rootNode = d3.hierarchy(data);
    const dx = 120; // Increased horizontal spacing between nodes to prevent overlap
    const dy = (height - marginTop - marginBottom) / (1 + rootNode.height); // Vertical spacing between levels

    // Define the tree layout and the shape for links
    const tree = d3.tree().nodeSize([dx, dy]);
    const diagonal = d3.linkVertical().x((d: any) => d.x).y((d: any) => d.y);

    const colorManager = colorManagerRef.current;

    // Function to calculate node radius based on number of children
    const getNodeRadius = (d: any) => {
      if (d.depth === 0) return 25; // Root node - largest
      
      const childCount = d.data.children ? d.data.children.length : 0;
      const baseRadius = 8;
      const maxRadius = 20;
      
      // Scale radius based on number of children (logarithmic scale for better distribution)
      if (childCount === 0) return baseRadius;
      return Math.min(baseRadius + Math.log2(childCount + 1) * 3, maxRadius);
    };

    // Function to get node color
    const getNodeColorForNode = (d: any) => getNodeColor(d, colorManager);

    // Create the SVG container
    const svgBg = isDarkTheme 
      ? 'background: linear-gradient(135deg, #0d1f2f 0%, #142737 50%, #0d1f2f 100%);' 
      : `background: linear-gradient(135deg, ${COLORS.BACKGROUND_LIGHT} 0%, ${COLORS.BORDER_GRAY} 100%);`;
    svg
      .attr("width", "100%")
      .attr("height", "100%")
      .attr("viewBox", [-width/2, -marginTop, width, height])
      .attr("style", `max-width: 100%; height: 82%; font: 12px sans-serif; user-select: none; ${svgBg}`);

    // Add zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([ZOOM_CONFIG.MIN_SCALE, ZOOM_CONFIG.MAX_SCALE])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
        setZoomLevel(event.transform.k);
      });

    svg.call(zoom as any);
    zoomRef.current = zoom;

    // Initialize zoom utilities
    zoomUtilsRef.current = new ZoomUtils({
      svgRef: svgRef,
      zoomRef: zoomRef
    });

    // Create a container group for all elements
    const container = svg.append("g");

    const gLink = container.append("g")
      .attr("fill", "none")
      .attr("stroke", isDarkTheme ? "rgba(255,255,255,0.4)" : COLORS.STROKE_COLOR)
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", 2);

    const gNode = container.append("g")
      .attr("cursor", "pointer")
      .attr("pointer-events", "all");

    function update(event: any, source: any) {
      const transitionDuration = event?.altKey ? 2500 : duration;
      const nodes = rootNode.descendants().reverse();
      const links = rootNode.links();

      // Compute the new tree layout
      tree(rootNode as any);

      let left = rootNode;
      let right = rootNode;
      rootNode.eachBefore((node: any) => {
        if (node.x! < left.x!) left = node;
        if (node.x! > right.x!) right = node;
      });

      const newWidth = right.x! - left.x! + marginLeft + marginRight;

      // Create transition with unique ID to avoid conflicts
      const transitionId = `transition_${Date.now()}_${Math.random()}`;
      const transition = container.transition(transitionId)
        .duration(transitionDuration);

      // Update the nodes
      const node = gNode.selectAll("g")
        .data(nodes, (d: any) => d.id);

      // Enter any new nodes at the parent's previous position
      const nodeEnter = node.enter().append("g")
        .attr("transform", d => `translate(${source.x0},${source.y0})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0)
        .style("cursor", "pointer")
        .on("click", (event: any, d: any) => {
          const wasExpanded = !!d.children;
          const willExpand = !wasExpanded && !!(d as any)._children;
          const transitionDuration = event?.altKey ? 2500 : duration;
          
          d.children = d.children ? null : (d as any)._children;
          update(event, d);
          saveExpandedState(rootNode);
          
          // Apply zoom adjustment and pan to show children using utility
          if (zoomUtilsRef.current) {
            if (willExpand && d.children) {
              // When expanding, ensure children are visible on screen
              zoomUtilsRef.current.ensureChildrenVisible(d, transitionDuration);
            }
            // Apply zoom adjustment for both expand and collapse
            zoomUtilsRef.current.applyManualClickZoom(willExpand);
          }
        })
        .on("mouseover", function(event: any, d: any) {
          if (d.depth === 0) {
            // Root node - scale the logo
            d3.select(this).select("image")
              .transition()
              .duration(200)
              .attr("width", 96)
              .attr("height", 96)
              .attr("x", -48)
              .attr("y", -48)
              .style("filter", isDarkTheme
                ? `invert(1) drop-shadow(0 4px 8px ${COLORS.DROP_SHADOW_STRONG})`
                : `drop-shadow(0 4px 8px ${COLORS.DROP_SHADOW_STRONG})`);
          } else if (d.data.type === 'api') {
            // API nodes - scale the rectangle
            d3.select(this).select("rect")
              .transition()
              .duration(200)
              .attr("width", 48)
              .attr("height", 24)
              .attr("x", -24)
              .attr("y", -12)
              .style("filter", `drop-shadow(0 4px 8px ${COLORS.DROP_SHADOW_STRONG})`);
          } else {
            // Area nodes - scale the circle
            d3.select(this).select("circle")
              .transition()
              .duration(200)
              .attr("r", (d: any) => getNodeRadius(d) * 1.1)
              .style("filter", `drop-shadow(0 4px 8px ${COLORS.DROP_SHADOW_STRONG})`);
          }
          
          // Show tooltip only for API nodes (not for long names - those will be handled by text mouseover)
          if (d.data.type === 'api' || d.data.description || d.data.developerProgram !== undefined || d.data.latestApiVersionNumber) {
            showTooltip(event, d);
          }
        })
        .on("mouseout", function(event: any, d: any) {
          if (d.depth === 0) {
            // Root node - reset the logo
            d3.select(this).select("image")
              .transition()
              .duration(200)
              .attr("width", 80)
              .attr("height", 80)
              .attr("x", -40)
              .attr("y", -40)
              .style("filter", isDarkTheme
                ? `invert(1) drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`
                : `drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`);
          } else if (d.data.type === 'api') {
            // API nodes - reset the rectangle
            d3.select(this).select("rect")
              .transition()
              .duration(200)
              .attr("width", 40)
              .attr("height", 20)
              .attr("x", -20)
              .attr("y", -10)
              .style("filter", `drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`);
          } else {
            // Area nodes - reset the circle
            d3.select(this).select("circle")
              .transition()
              .duration(200)
              .attr("r", (d: any) => getNodeRadius(d))
              .style("filter", `drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`);
          }
          
          // Hide tooltip with delay
          hideTooltip();
        });

      // Add shapes for non-root nodes and logo for root node
      nodeEnter.each(function(d: any) {
        const node = d3.select(this);
        
        if (d.depth === 0) {
          // Root node - add Motorola logo with optional pulsing animation
          node.append("image")
            .attr("href", motoLogo)
            .attr("x", -40)
            .attr("y", -40)
            .attr("width", 80)
            .attr("height", 80)
            .style("filter", isDarkTheme
              ? `invert(1) drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`
              : `drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`)
            .attr("class", isPulsing ? "root-node-pulse" : "");
        } else if (d.data.type === 'api') {
          // API nodes - add rectangle with medium size
          const rectWidth = 40;
          const rectHeight = 20;
          const rect = node.append("rect")
            .attr("x", -rectWidth/2)
            .attr("y", -rectHeight/2)
            .attr("width", rectWidth)
            .attr("height", rectHeight)
            .attr("rx", 4) // Rounded corners
            .attr("ry", 4)
            .attr("fill", (d: any) => getNodeColorForNode(d))
            .attr("stroke", isDarkTheme ? "rgba(255,255,255,0.5)" : COLORS.WHITE)
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "2")
            .style("filter", `drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`)
            .style("transition", "all 0.3s ease");
            
          // Add pulsing animation for API nodes
          if (isPulsing) {
            rect.attr("class", `api-node-pulse ${d.data.developerProgram ? 'developer-program' : 'standard'}`);
          }
        } else {
          // Non-API area nodes - add circle with optional pulsing animation
          const circle = node.append("circle")
            .attr("r", (d: any) => getNodeRadius(d))
            .attr("fill", (d: any) => getNodeColorForNode(d))
            .attr("stroke", isDarkTheme ? "#ffffff" : COLORS.WHITE)
            .attr("stroke-width", 3)
            .style("filter", `drop-shadow(0 2px 4px ${COLORS.DROP_SHADOW_NORMAL})`)
            .style("transition", "all 0.3s ease");
            
          // Add pulsing animation for area nodes
          if (isPulsing) {
            circle.attr("class", "pulse-node");
          }
        }
      });

      nodeEnter.append("text")
        .attr("dy", "0.31em")
        .attr("y", (d: any) => {
          const radius = getNodeRadius(d);
          // Increase spacing to prevent overlap, especially for leaf nodes
          const spacing = d.children || (d as any)._children ? -(radius + 15) : (radius + 20);
          return spacing;
        })
        .attr("text-anchor", "middle")
        .text((d: any) => {
          // Truncate long names to prevent overlap
          const name = d.data.name;
          return name.length > MAX_NODE_NAME_LENGTH ? name.substring(0, TRUNCATED_NAME_LENGTH) + "..." : name;
        })
        .attr("stroke-linejoin", "round")
        .attr("stroke-width", 3)
        .attr("stroke", isDarkTheme ? "#142737" : COLORS.WHITE)
        .attr("paint-order", "stroke")
        .style("font-size", (d: any) => `${Math.max(9, Math.min(12, getNodeRadius(d) * 0.5))}px`)
        .style("font-weight", "600")
        .style("fill", isDarkTheme ? "#ffffff" : COLORS.TEXT_PRIMARY)
        .style("cursor", "pointer")
        .on("mouseover", function(event: any, d: any) {
          // Show tooltip for ALL long names when hovering over text (both API and non-API nodes)
          if (d.data.name.length > MAX_NODE_NAME_LENGTH) {
            showTooltip(event, d);
          }
        })
        .on("mouseout", function(event: any, d: any) {
          // Hide tooltip for ALL long names
          if (d.data.name.length > MAX_NODE_NAME_LENGTH) {
            hideTooltip();
          }
        });

      // Add child count text inside larger circles
      nodeEnter.filter((d: any) => d.depth > 0 && d.data.children && d.data.children.length > 0)
        .append("text")
        .attr("dy", "0.31em")
        .attr("text-anchor", "middle")
        .text((d: any) => d.data.children?.length || 0)
        .style("font-size", (d: any) => `${Math.max(8, getNodeRadius(d) * 0.4)}px`)
        .style("font-weight", "bold")
        .style("fill", COLORS.WHITE)
        .style("pointer-events", "none");

      // Transition nodes to their new position
      const nodeUpdate = (node as any).merge(nodeEnter).transition(transition as any)
        .attr("transform", (d: any) => `translate(${d.x},${d.y})`)
        .attr("fill-opacity", 1)
        .attr("stroke-opacity", 1);

      // Update shapes colors and sizes based on node type
      nodeUpdate.select("circle")
        .attr("fill", (d: any) => getNodeColorForNode(d))
        .attr("r", (d: any) => getNodeRadius(d));
      
      nodeUpdate.select("rect")
        .attr("fill", (d: any) => getNodeColorForNode(d));

      // Update text position based on whether node has children
      nodeUpdate.select("text")
        .attr("y", (d: any) => {
          const radius = getNodeRadius(d);
          // Consistent spacing with nodeEnter
          const spacing = d.children || (d as any)._children ? -(radius + 15) : (radius + 20);
          return spacing;
        })
        .attr("text-anchor", "middle")
        .text((d: any) => {
          // Use same logic as nodeEnter for consistency
          const name = d.data.name;
          return name.length > MAX_NODE_NAME_LENGTH ? name.substring(0, TRUNCATED_NAME_LENGTH) + "..." : name;
        })
        .style("font-size", (d: any) => `${Math.max(9, Math.min(12, getNodeRadius(d) * 0.5))}px`);

      // Add simple title tooltips for truncated text
      (node as any).merge(nodeEnter).each(function(this: any, d: any) {
        const name = d.data.name;
        
        if (name.length > MAX_NODE_NAME_LENGTH) {
          const textElements = d3.select(this).selectAll("text");
          
          textElements.each(function() {
            const content = d3.select(this).text();
            const hasCorrectDy = d3.select(this).attr("dy") === "0.31em";
            const hasCorrectAnchor = d3.select(this).attr("text-anchor") === "middle";
            const isMainText = content.includes(name.substring(0, 10)) || content === name;
            
            if (hasCorrectDy && hasCorrectAnchor && isMainText) {
              d3.select(this).append("title").text(name);
            }
          });
        }
      });

      // Update child count text
      nodeUpdate.selectAll("text:nth-child(3)")
        .text((d: any) => d.data.children ? d.data.children.length : 0)
        .style("font-size", (d: any) => `${Math.max(8, getNodeRadius(d) * 0.4)}px`);

      // Transition exiting nodes to the parent's new position
      const nodeExit = (node as any).exit().transition(transition as any).remove()
        .attr("transform", (d: any) => `translate(${source.x},${source.y})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0);

      // Update the links
      const link = gLink.selectAll("path")
        .data(links, (d: any) => d.target.id);

      // Enter any new links at the parent's previous position
      const linkEnter = link.enter().append("path")
        .attr("d", (d: any) => {
          const o = {x: source.x0, y: source.y0};
          return diagonal({source: o, target: o} as any);
        });

      // Transition links to their new position
      (link as any).merge(linkEnter).transition(transition as any)
        .attr("d", diagonal as any);

      // Transition exiting links to the parent's new position
      (link as any).exit().transition(transition as any).remove()
        .attr("d", (d: any) => {
          const o = {x: source.x, y: source.y};
          return diagonal({source: o, target: o} as any);
        });

      // Stash the old positions for transition
      rootNode.eachBefore((d: any) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    }

    // Store the update function for external access
    updateRef.current = update;

    // Initialize the tree
    (rootNode as any).x0 = 0;
    (rootNode as any).y0 = marginTop;

    // Try to restore previously expanded state from sessionStorage
    const previouslyExpanded = loadExpandedState();

    rootNode.descendants().forEach((d: any, i: number) => {
      d.id = i;
      (d as any)._children = d.children;

      if (previouslyExpanded) {
        // Restore: expand only nodes that were previously expanded
        const key = getNodeKey(d);
        if (!previouslyExpanded.has(key) && d.depth >= 1) {
          d.children = null;
        }
      } else {
        // Default: collapse all nodes at depth >= 1
        if (d.depth >= 1) d.children = null;
      }
    });

    update(null, rootNode);
    setRoot(rootNode);

    // Set default zoom to 150%
    svg.call(zoom.transform as any, d3.zoomIdentity.scale(ZOOM_CONFIG.DEFAULT_SCALE));

    // Update legend items based on the current data
    const dynamicLegendItems = generateLegendItems(rootNode, colorManager);
    setLegendItems(dynamicLegendItems);

  }, [data, width, height, duration, marginTop, marginRight, marginBottom, marginLeft, isDarkTheme]);

  // Cleanup effect to clear timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTransitions();
    };
  }, []);

  const expandOneLevel = () => {
    if (!root || !updateRef.current) return;
    
    // Function to check if a node is currently visible in the tree
    const isNodeVisible = (node: any) => {
      if (node.depth === 0) return true; // Root is always visible
      
      let current = node;
      while (current.parent) {
        // Check if current node is in its parent's children array
        if (!current.parent.children || !current.parent.children.includes(current)) {
          return false;
        }
        current = current.parent;
      }
      return true;
    };

    // Find all currently visible nodes that have collapsed children
    const visibleNodes = root.descendants().filter((d: any) => isNodeVisible(d));
    const nodesToExpand = visibleNodes.filter((d: any) => (d as any)._children && !d.children);

    // Expand only the collapsed children of visible nodes
    if (nodesToExpand.length > 0) {
      nodesToExpand.forEach((d: any) => {
        d.children = (d as any)._children;
      });

      safeUpdate(root);
      saveExpandedState(root);
      
      // Apply zoom using utility
      if (zoomUtilsRef.current) {
        zoomUtilsRef.current.applyExpandOneLevelZoom(nodesToExpand.length);
        
        if (nodesToExpand[0].children) {
          zoomUtilsRef.current.ensureChildrenVisible(nodesToExpand[0], duration);
        }
      }
    }
  };

  const expandAll = () => {
    if (!root || !updateRef.current) return;
    
    // Expand ALL nodes recursively
    const expandAllNodes = (node: any) => {
      if ((node as any)._children) {
        node.children = (node as any)._children;
        node.children.forEach((child: any) => expandAllNodes(child));
      }
    };
    
    expandAllNodes(root);
    safeUpdate(root);
    saveExpandedState(root);
    
    // Apply zoom using utility
    if (zoomUtilsRef.current) {
      zoomUtilsRef.current.applyExpandAllZoom();
    }
  };

  const collapseAll = () => {
    if (!root || !updateRef.current) return;
    
    root.descendants().forEach((d: any) => {
      if (d.children) {
        (d as any)._children = d.children;
        if (d.depth > 0) d.children = null;
      }
    });
    saveExpandedState(root);
    
    safeUpdate(root);
    
    // Reset zoom to default using utility
    if (zoomUtilsRef.current) {
      zoomUtilsRef.current.resetToDefault();
    }
  };

  const zoomIn = () => {
    if (zoomUtilsRef.current) {
      zoomUtilsRef.current.zoomIn();
    }
  };

  const zoomOut = () => {
    if (zoomUtilsRef.current) {
      zoomUtilsRef.current.zoomOut();
    }
  };

  const resetZoom = () => {
    if (zoomUtilsRef.current) {
      zoomUtilsRef.current.resetZoom();
    }
  };

  const togglePulsing = () => {
    setIsPulsing(!isPulsing);
    
    // Update existing nodes
    if (svgRef.current) {
      const svg = d3.select(svgRef.current);
      
      // Toggle pulsing classes on all node types
      svg.selectAll('.root-node-pulse')
        .classed('root-node-pulse', isPulsing ? false : true);
      
      svg.selectAll('.api-node-pulse')
        .classed('api-node-pulse', isPulsing ? false : true);
      
      svg.selectAll('.pulse-node')
        .classed('pulse-node', isPulsing ? false : true);
    }
  };

  const toggleLegend = () => {
    setIsLegendCollapsed(!isLegendCollapsed);
  };

  return (
    <div className="diagram-v2">
      {/* Legend and Controls - moved to top */}
      <div className="diagram-header">
        <div className="header-content">
          <p className="header-text">
            <strong>API Hub Diagram View:</strong> Click nodes to expand/collapse • Drag to pan • Scroll to zoom • Hold Alt while clicking for slow transitions
          </p>
          
          {/* Controls */}
          <div className="controls-container">
            <button
              onClick={expandOneLevel}
              className="diagram-button diagram-button--primary"
            >
              Expand One Level
            </button>
            <button
              onClick={expandAll}
              className="diagram-button diagram-button--info"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="diagram-button diagram-button--secondary"
            >
              Collapse All
            </button>
            <button
              onClick={toggleLegend}
              className="diagram-button diagram-button--success"
              title={isLegendCollapsed ? 'Show Legend' : 'Hide Legend'}
            >
              {isLegendCollapsed ? '▲ Show Legend' : '▼ Hide Legend'}
            </button>
          </div>
        </div>
        
        {!isLegendCollapsed && (
          <div className="legend-container">
            <span className="legend-title">Node Types:</span>
            <div className="legend-item">
              <img 
                src={motoLogo} 
                alt="MSI Logo" 
                className="legend-icon legend-icon--logo"
                style={isDarkTheme ? { filter: 'invert(1)' } : undefined}
              />
              <span className="legend-text">Root (Motorola Solutions)</span>
            </div>
            
            {/* Dynamic Area Legend */}
            {legendItems.map((item, index) => (
              <div key={item.name} className="legend-item">
                <div 
                  className="legend-icon"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="legend-text legend-text--small">
                  {item.name.length > 15 ? item.name.substring(0, 12) + '...' : item.name}
                </span>
              </div>
            ))}
            
            <div className="legend-item">
              <div className="legend-icon legend-icon--api"></div>
              <span className="legend-text">APIs</span>
            </div>
            <span className="legend-description">
              <strong>Size:</strong> Based on children count • <strong>Gradient:</strong> Darker shades for deeper levels
            </span>
          </div>
        )}
      </div>

      <div className="diagram-container">
        <svg 
          ref={svgRef}
          className="diagram-svg"
          style={{ 
            background: isDarkTheme 
              ? '#000912'
              : `linear-gradient(135deg, ${COLORS.BACKGROUND_LIGHT} 0%, ${COLORS.BORDER_GRAY} 100%)`
          }}
        />
        
        {/* Tooltip */}
        <DiagramTooltip
          tooltip={tooltip}
          onMouseEnter={() => {
            // Cancel hide timeout when entering tooltip
            clearTimeout((window as any).tooltipTimeout);
            keepTooltipVisible();
          }}
          onMouseLeave={() => {
            // Hide tooltip when leaving tooltip area
            setTooltip(null);
          }}
          onViewDetails={handleViewDetails}
        />
      </div>
    </div>
  );
};

export default DiagramV2;
